/**
 * Utility function to linear interpolate values.
 *
 * @param {number} start - Value to lerp.
 * @param {number} end - Target value.
 * @param {number} amount - Easing.
 *
 * @returns {number} Lerped value.
 */
export function lerp(start, end, amt) {
	return (1 - amt) * start + amt * end;
}

/**
 * Utility function to get width with margins included.
 *
 * @param {HTMLElement} el - Element you want to calculate the outer width of.
 *
 * @returns {number} - The element's outer with.
 */
export function outerWidth(el) {
	var width = el.offsetWidth;
	var style = getComputedStyle(el);

	width += parseInt(style.marginLeft) + parseInt(style.marginRight);
	return width;
}

/**
 * Shortcut for rounding number to second decimal place.
 *
 * @param {number} numer - Number to round.
 *
 * @returns {number} - Rounded number to second decimal place.
 */
export function round(number) {
	return Math.round((number + Number.EPSILON) * 100) / 100;
}

/**
 * Utility function to check of the device is a touch device.
 *
 * @returns {boolean} - If device is a touch device or not.
 */
export function isTouchDevice() {
	return "ontouchstart" in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0;
}

/**
 * Throws an error if the provided condition is falsy.
 *
 * @param {boolean} condition - The condition to assert.
 * @param {string} [message] - Optional error message to display if the assertion fails.
 *
 * @throws {Error} - Throws an Error if the condition is falsy.
 */
export function assert(condition, message) {
	if (!condition) {
		throw new Error(message ?? `Assertion failed: ${condition}`);
	}
}

/**
 * Waits for an element to exist in the DOM and executes a callback.
 * * @param {string} selector - The CSS selector to watch for.
 * @param {function} callback - Function to execute once found.
 * @param {Element} [parentElement=document.body] - Scope to watch.
 */
export function waitForElement(selector, callback, parentElement = document.body) {
	// 1. Check if the element already exists immediately
	const element = document.querySelector(selector);
	if (element) {
		callback(element);
		return;
	}

	// 2. If not, observe the DOM for changes
	const observer = new MutationObserver((mutations, obs) => {
		const targetElement = document.querySelector(selector);

		if (targetElement) {
			callback(targetElement);
			obs.disconnect(); // Stop observing once found
		}
	});

	observer.observe(parentElement, {
		childList: true,
		subtree: true,
	});
}
